import { GeneratorPaths } from 'constants/routes';
import { ReactNode, RefObject, createContext, useContext, useMemo, useRef, useState } from 'react';

export enum DesignAICategories {
  Charts = 'charts',
  Infographics = 'infographics',
  Posters = 'posters',
}

interface DesignAIContextType {
  category: DesignAICategories;
  setCategory: (category: DesignAICategories) => void;
  redirectToTemplates: () => void;
  firstFocusableElementRef: RefObject<HTMLButtonElement>;
  lastFocusableElementRef: RefObject<HTMLButtonElement>;
}

interface DesignAIContextProps {
  children: ReactNode;
}

const DesignAIContext = createContext<DesignAIContextType | undefined>(undefined);

export const DesignAIProvider = ({ children }: DesignAIContextProps) => {
  const urlPath = window.location.pathname.replace(/^\/+/g, '');
  let initialCategory = DesignAICategories.Infographics;
  switch (urlPath) {
    case GeneratorPaths.Infographic:
      initialCategory = DesignAICategories.Infographics;
      break;
    case GeneratorPaths.Poster:
      initialCategory = DesignAICategories.Posters;
      break;
    case GeneratorPaths.Chart:
      initialCategory = DesignAICategories.Charts;
      break;
    default:
      break;
  }
  const [category, setCategory] = useState<DesignAICategories>(initialCategory);

  const firstFocusableElementRef = useRef<HTMLButtonElement>(null);
  const lastFocusableElementRef = useRef<HTMLButtonElement>(null);

  const value = useMemo(() => {
    const redirectToTemplates = () => (window.location.href = `/templates/${category}/generate`);

    return {
      category,
      setCategory,
      redirectToTemplates,
      firstFocusableElementRef,
      lastFocusableElementRef,
    };
  }, [category]);

  return <DesignAIContext.Provider value={value}>{children}</DesignAIContext.Provider>;
};

export const useDesignAIContext = () => {
  const context = useContext(DesignAIContext);
  if (!context) {
    throw new Error('useDesignAIContext must be used within DesignAIProvider');
  }
  return context;
};
